import { useCallback, useMemo } from 'react';
import { Table } from '@carboledger/ui';
import { buildTableLabels } from '@/config/tableLabels';
import {
  useTableState,
  useTranslations,
  useGenuinelyEmpty,
  UnitTypeContext,
  useUnitTypeHeader,
} from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import {
  useGetOutgoingShipmentsQuery,
  useGetOutgoingShipmentFilterOptionsQuery,
} from '../api/outgoingShipmentsApi';
import { buildOutgoingShipmentsColumns } from '../constants/outgoingShipmentsColumns';
import { buildOutgoingShipmentFilterDefinitions } from '../constants/outgoingShipmentFilters';
import { buildUnitHeaderOptions } from '../constants/outgoingShipmentUnits';

const getOutgoingShipmentRowId = (row) => row.id;

/**
 * Props:
 *   extraFilters             filters merged on top of the panel's, e.g. the inventory drill-down's
 *   certificateType   the module's `CertificateType`, which narrows the list
 *   emptyState        <Table>'s emptyState — the caller owns its copy and CTA
 *   noResults         <Table>'s noResults
 *   onOpenShipment    (row) => void — makes the id cell a link; omit for plain text
 *   onOpenDiscrepancy (row, unitType) => void — the row's discrepancy modal
 *   onEmptyChange     (isEmpty) => void — true only when nothing matches and nothing is filtered
 *   rowActions        <Table>'s rowActions
 *   rowMenuActions    <Table>'s rowMenuActions
 *   bulkActions       <Table>'s bulkActions — selectable follows from it
 *   tableRef          forwarded to <Table>, for clearSelection
 */
export const OutgoingShipmentsTable = ({
  extraFilters,
  certificateType,
  emptyState,
  noResults,
  onOpenShipment,
  onOpenDiscrepancy,
  onEmptyChange,
  rowActions,
  rowMenuActions,
  bulkActions,
  tableRef,
}) => {
  const t = useTranslations();
  const table = useTableState();

  const queryArgs = useMemo(
    () => ({ certificateType, ...table.queryParams, extraFilters }),
    [certificateType, table.queryParams, extraFilters],
  );
  const { data, isFetching, isError } = useGetOutgoingShipmentsQuery(queryArgs);
  const rows = data?.rows ?? EMPTY_LIST;
  const total = data?.totalCount ?? 0;

  useGenuinelyEmpty({ table, isFetching, isError, total }, onEmptyChange);

  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(
    rows,
    buildUnitHeaderOptions,
    t,
  );

  // The modal needs the unit the row is currently showing, which only the table knows.
  const handleOpenDiscrepancy = useCallback(
    (row) => onOpenDiscrepancy?.(row, selections.get(row.id) ?? row.unitType),
    [onOpenDiscrepancy, selections],
  );

  const columns = useMemo(
    () =>
      buildOutgoingShipmentsColumns(t, {
        unitHeaderSelect,
        onOpenShipment,
        onOpenDiscrepancy: handleOpenDiscrepancy,
      }),
    [t, unitHeaderSelect, onOpenShipment, handleOpenDiscrepancy],
  );

  const filterOptionsArgs = useMemo(
    () => ({ certificateType, extraFilters }),
    [certificateType, extraFilters],
  );
  const { data: filterOptions } = useGetOutgoingShipmentFilterOptionsQuery(filterOptionsArgs);

  const filterDefinitions = useMemo(
    () =>
      buildOutgoingShipmentFilterDefinitions(t, {
        materialOptions: filterOptions?.materialOptions ?? EMPTY_LIST,
        customerOptions: filterOptions?.customerOptions ?? EMPTY_LIST,
      }),
    [t, filterOptions],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  // <Table> reports sort as { id, dir }; useTableState stores { field, direction }.
  const handleSortChange = useCallback(
    (next) => {
      table.setSort(next ? { field: next.id, direction: next.dir } : null);
      table.setPage(1);
    },
    [table],
  );

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const handleFilterChange = useCallback(
    (values) => {
      table.setFilters(values);
      table.setPage(1);
    },
    [table],
  );

  return (
    <UnitTypeContext.Provider value={contextValue}>
      <Table
        ref={tableRef}
        columns={columns}
        data={rows}
        total={total}
        loading={isFetching}
        getRowId={getOutgoingShipmentRowId}
        searchable
        selectable={Boolean(bulkActions)}
        bulkActions={bulkActions}
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        filters={filterDefinitions}
        onFilterChange={handleFilterChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        actionsHeader={t('FIELD_ACTION')}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />
    </UnitTypeContext.Provider>
  );
};
